import { useCallback, useEffect, useState } from "react";
import { BadgeDollarSign, Check, Copy, Download, Handshake, Mail, Pause, Play, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/data/networks";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLE = { pending: "bg-[#FDEEE8] text-[#9C421E]", active: "bg-[#1F1A17] text-[#FBF8F4]", paused: "bg-[#F3ECE5] text-[#685F58]", rejected: "bg-[#F3ECE5] text-[#685F58] line-through" };

const hookNote = (origin) => `HearthVault partner tracking — developer note
=============================================

1) On hearthvault.family/welcome, read the query params and store them on the new user:
   utm_source, utm_medium, utm_campaign, utm_content, ref   (ref = partner code, e.g. "moneymom")

2) When a referred user (ref is set) signs up / activates / pays, POST to:
   ${origin}/api/partners/conversions
   Authorization: Bearer <PARTNER_WEBHOOK_SECRET from the Hearthside backend .env>
   Content-Type: application/json

   {
     "ref": "moneymom",
     "event": "signup" | "activated" | "payment" | "refund",
     "user_id": "<your user id>",
     "amount": 12.00,            // payments/refunds only, in USD
     "event_id": "<unique id, e.g. Stripe invoice id>",   // makes retries safe
     "occurred_at": "2026-11-20T10:15:00Z"               // optional
   }

   "activated" = whatever you define as a real user (e.g. 3 documents uploaded or day-14 retention).
   Commission is computed here: commission % of payments within the commission window, plus the milestone bonus.

3) Responses: 202 {"status":"accepted"} · 202 {"status":"duplicate"} · 401 bad secret · 404 unknown code.
`;

const Metric = ({ label, value }) => (
  <div className="text-right">
    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#685F58]">{label}</div>
    <div className="font-serif text-xl text-[#1F1A17]">{value}</div>
  </div>
);

const PayoutLog = ({ p, onAdd, onDelete }) => {
  const m = p.metrics;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(m.owed > 0 ? m.owed.toFixed(2) : "");
  const [period, setPeriod] = useState(new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }));
  const [note, setNote] = useState("");
  const submit = async (e) => { e.preventDefault(); await onAdd(p.id, { amount: Number(amount), period, note }); setOpen(false); setNote(""); };
  const pct = m.earned > 0 ? Math.min(100, (m.paid / m.earned) * 100) : 0;
  return (
    <div className="mt-4 rounded-2xl bg-[#FBF8F4] border border-[#E8DDD3] p-4" data-testid={`partner-payouts-${p.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-xs">
          <span className="text-[#685F58]">Earned <strong className="text-[#1F1A17]">${m.earned.toFixed(2)}</strong></span>
          <span className="text-[#685F58]">Paid <strong className="text-[#1F1A17]" data-testid={`partner-paid-${p.id}`}>${m.paid.toFixed(2)}</strong></span>
          <span className={m.owed > 0 ? "text-[#9C421E]" : "text-[#685F58]"}>Balance <strong data-testid={`partner-balance-${p.id}`}>${m.owed.toFixed(2)}</strong></span>
        </div>
        <button onClick={() => setOpen((v) => !v)} data-testid={`partner-record-payout-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-8 px-3 text-xs text-[#FBF8F4] transition-colors"><BadgeDollarSign className="h-3.5 w-3.5" /> Record payout</button>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[#F3ECE5] overflow-hidden"><div className="h-full rounded-full bg-[#1F1A17]" style={{ width: `${pct}%` }} /></div>
      {open && (
        <form onSubmit={submit} className="mt-3 grid sm:grid-cols-[120px_1fr_1fr_auto] gap-2" data-testid={`partner-payout-form-${p.id}`}>
          <input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" data-testid={`partner-payout-amount-${p.id}`} className="h-9 rounded-full border border-[#E8DDD3] bg-white px-3 text-xs text-[#1F1A17] outline-none focus:border-[#1F1A17]" />
          <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Period (e.g. November 2026)" data-testid={`partner-payout-period-${p.id}`} className="h-9 rounded-full border border-[#E8DDD3] bg-white px-3 text-xs text-[#1F1A17] outline-none focus:border-[#1F1A17]" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (PayPal ref, bank…)" data-testid={`partner-payout-note-${p.id}`} className="h-9 rounded-full border border-[#E8DDD3] bg-white px-3 text-xs text-[#1F1A17] outline-none focus:border-[#1F1A17]" />
          <button type="submit" data-testid={`partner-payout-submit-${p.id}`} className="h-9 rounded-full bg-[#B4532A] hover:bg-[#9C421E] px-4 text-xs font-medium text-[#FBF8F4] transition-colors">Mark paid</button>
        </form>
      )}
      {p.payouts.length > 0 && (
        <ul className="mt-3 divide-y divide-[#E8DDD3]" data-testid={`partner-payout-history-${p.id}`}>
          {p.payouts.map((x) => (
            <li key={x.id} className="flex items-center justify-between py-2 text-xs">
              <span className="text-[#1F1A17]"><strong>${x.amount.toFixed(2)}</strong> <span className="text-[#685F58]">· {x.period || "—"} · {x.paidAt.slice(0, 10)}{x.note ? ` · ${x.note}` : ""}</span></span>
              <button onClick={() => onDelete(p.id, x.id)} aria-label="Remove payout" data-testid={`partner-payout-delete-${x.id}`} className="text-[#685F58] hover:text-[#B4532A]"><Trash2 className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Row = ({ p, onChange, onResendWelcome, onAddPayout, onDeletePayout, onCopyPortal }) => {
  const m = p.metrics;
  const link = `${SITE}/welcome?utm_source=partner&utm_medium=partner&utm_campaign=hearthside&ref=${p.code}`;
  const landing = `${window.location.origin}/?ref=${p.code}`;
  const [code, setCode] = useState(p.code || p.requestedCode || "");
  const copy = async (t) => { await navigator.clipboard.writeText(t); toast.success("Copied"); };
  return (
    <div className="rounded-2xl border border-[#E8DDD3] bg-white p-5" data-testid={`partner-row-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#1F1A17]">{p.name}</span>
            <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[p.status]}`} data-testid={`partner-status-${p.id}`}>{p.status}</span>
          </div>
          <div className="mt-1 text-xs text-[#685F58]">{p.platform} · {p.handle || "—"} · {p.audience || "—"} · {p.email}</div>
          {p.status !== "pending" && (
            <div className="mt-1 flex flex-wrap gap-x-4 text-[11px]" data-testid={`partner-email-status-${p.id}`}>
              <span className={p.welcomedAt ? "text-[#1F1A17]" : "text-[#9C421E]"}>Welcome: {p.welcomedAt ? `sent ${p.welcomedAt.slice(0, 10)}` : p.welcomedAtStatus || "not sent"}</span>
              <span className="text-[#685F58]">Statement: {p.lastStatementAt ? `sent ${p.lastStatementAt.slice(0, 10)}` : p.lastStatementAtStatus || "none yet"}</span>
            </div>
          )}
          {p.why && <p className="mt-2 max-w-2xl text-xs leading-relaxed text-[#685F58] italic">“{p.why}”</p>}
        </div>
        <div className="flex gap-2">
          {p.status === "pending" && (
            <>
              <button onClick={() => onChange(p.id, { status: "active", code: code || undefined })} data-testid={`partner-approve-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-9 px-3.5 text-xs font-medium text-[#FBF8F4] transition-colors"><Check className="h-3.5 w-3.5" /> Approve</button>
              <button onClick={() => onChange(p.id, { status: "rejected" })} data-testid={`partner-reject-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DDD3] h-9 px-3.5 text-xs text-[#685F58] hover:text-[#1F1A17] transition-colors"><X className="h-3.5 w-3.5" /> Decline</button>
            </>
          )}
          {p.status === "active" && <button onClick={() => onResendWelcome(p.id)} data-testid={`partner-resend-welcome-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DDD3] h-9 px-3.5 text-xs text-[#685F58] hover:text-[#1F1A17] transition-colors"><Mail className="h-3.5 w-3.5" /> {p.welcomedAt ? "Resend welcome" : "Send welcome"}</button>}
          {p.status === "active" && <button onClick={() => onChange(p.id, { status: "paused" })} data-testid={`partner-pause-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DDD3] h-9 px-3.5 text-xs text-[#685F58] hover:text-[#1F1A17] transition-colors"><Pause className="h-3.5 w-3.5" /> Pause</button>}
          {(p.status === "paused" || p.status === "rejected") && <button onClick={() => onChange(p.id, { status: "active" })} data-testid={`partner-resume-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-9 px-3.5 text-xs font-medium text-[#FBF8F4] transition-colors"><Play className="h-3.5 w-3.5" /> Activate</button>}
        </div>
      </div>

      {p.status === "pending" ? (
        <label className="mt-4 flex items-center gap-3 text-xs text-[#685F58]">Code
          <input value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))} placeholder="auto from name" maxLength={24} data-testid={`partner-code-input-${p.id}`} className="h-9 w-44 rounded-full border border-[#E8DDD3] bg-[#FBF8F4] px-3 font-mono text-xs text-[#1F1A17] outline-none focus:border-[#1F1A17]" />
        </label>
      ) : (
        <div className="mt-4 grid lg:grid-cols-[1fr_auto] gap-4 items-end">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono rounded-full bg-[#F3ECE5] px-3 py-1.5 text-[#1F1A17]" data-testid={`partner-code-${p.id}`}>ref={p.code}</span>
              <button onClick={() => copy(landing)} data-testid={`partner-copy-landing-${p.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#E8DDD3] h-8 px-3 text-[#1F1A17] hover:border-[#1F1A17]"><Copy className="h-3 w-3" /> Landing link</button>
              <button onClick={() => copy(link)} data-testid={`partner-copy-signup-${p.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#E8DDD3] h-8 px-3 text-[#1F1A17] hover:border-[#1F1A17]"><Copy className="h-3 w-3" /> Direct signup link</button>
              <button onClick={() => onCopyPortal(p.id)} data-testid={`partner-copy-portal-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FDEEE8] h-8 px-3 text-[#9C421E] hover:bg-[#B4532A] hover:text-white transition-colors"><Copy className="h-3 w-3" /> Their private dashboard</button>
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-4">
            <Metric label="Clicks" value={m.clicks} /><Metric label="Signup clicks" value={m.signup_clicks} /><Metric label="Signups" value={m.signups} /><Metric label="Active" value={m.activated} />
            <Metric label="Revenue" value={`$${m.revenue.toFixed(0)}`} /><Metric label="Bonus" value={`$${m.bonus.toFixed(0)}`} /><Metric label="Balance" value={`$${m.owed.toFixed(2)}`} />
          </div>
        </div>
      )}
      {p.status !== "pending" && <PayoutLog p={p} onAdd={onAddPayout} onDelete={onDeletePayout} />}
    </div>
  );
};

export const PartnersPanel = ({ pw }) => {
  const [data, setData] = useState(null);
  const [showHook, setShowHook] = useState(false);
  const headers = { "X-Stats-Key": pw, "Content-Type": "application/json" };

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/partners`, { headers });
    setData(await r.json());
  }, [pw]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const change = async (id, body) => {
    const r = await fetch(`${API}/api/partners/${id}`, { method: "PATCH", headers, body: JSON.stringify({ ...body, origin: window.location.origin }) });
    if (!r.ok) { const b = await r.json().catch(() => ({})); toast.error(b.detail || "Could not update"); return; }
    toast.success(body.status === "active" ? "Approved — welcome email on its way" : "Partner updated");
    setTimeout(load, 2500); load();
  };
  const resendWelcome = async (id) => {
    const r = await fetch(`${API}/api/partners/${id}/welcome`, { method: "POST", headers, body: JSON.stringify({ origin: window.location.origin }) });
    const b = await r.json().catch(() => ({}));
    r.ok ? toast.success("Welcome email sent") : toast.error(b.detail || "Send failed");
    load();
  };
  const addPayout = async (id, body) => {
    const r = await fetch(`${API}/api/partners/${id}/payouts`, { method: "POST", headers, body: JSON.stringify(body) });
    const b = await r.json().catch(() => ({}));
    r.ok ? toast.success(`Recorded $${Number(body.amount).toFixed(2)} payout`) : toast.error(b.detail || "Could not record payout");
    load();
  };
  const deletePayout = async (id, payoutId) => {
    const r = await fetch(`${API}/api/partners/${id}/payouts/${payoutId}`, { method: "DELETE", headers });
    r.ok ? toast.success("Payout removed") : toast.error("Could not remove payout");
    load();
  };
  const copyPortal = async (id) => {
    const r = await fetch(`${API}/api/partners/${id}/portal-link`, { method: "POST", headers });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(b.detail || "Could not create link"); return; }
    const url = b.url.startsWith("http") ? b.url : `${window.location.origin}${b.url}`;
    await navigator.clipboard.writeText(url); toast.success("Private dashboard link copied");
  };
  const [sendingStatements, setSendingStatements] = useState(false);
  const sendStatements = async () => {
    setSendingStatements(true);
    const r = await fetch(`${API}/api/partners/statements/send`, { method: "POST", headers });
    const b = await r.json().catch(() => ({}));
    setSendingStatements(false);
    r.ok ? toast.success(`Statements: ${b.sent} sent, ${b.failed} failed (${b.period})`) : toast.error(b.detail || "Could not send statements");
    load();
  };

  if (!data) return null;
  const pending = data.partners.filter((p) => p.status === "pending");
  const others = data.partners.filter((p) => p.status !== "pending");
  const owed = data.partners.reduce((s, p) => s + Math.max(0, p.metrics.owed), 0);
  const paid = data.partners.reduce((s, p) => s + p.metrics.paid, 0);
  const note = hookNote(API);

  return (
    <section className="mt-12 rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-partners">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl grid place-items-center bg-[#1F1A17] text-[#FBF8F4]"><Handshake className="h-4 w-4" /></span>
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-[#1F1A17]">Creators &amp; partners</h2>
            <p className="text-xs text-[#685F58]">Welcome email sent on approval · statements go out on the 1st at 09:00 UTC · {data.partners.length} applications · {data.partners.filter((p) => p.status === "active").length} active · ${paid.toFixed(2)} paid · ${owed.toFixed(2)} outstanding · terms {data.terms.commissionPct}% for {data.terms.commissionMonths} mo, ${data.terms.bonusAmount} at {data.terms.bonusThreshold}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={sendStatements} disabled={sendingStatements} data-testid="partners-send-statements" className="inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] disabled:opacity-60 h-10 px-4 text-sm text-[#FBF8F4] transition-colors"><Send className="h-4 w-4" /> {sendingStatements ? "Sending…" : "Send statements now"}</button>
          <a href={`${API}/api/partners/export.csv?key=${encodeURIComponent(pw)}`} data-testid="partners-export" className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] h-10 px-4 text-sm text-[#1F1A17] hover:border-[#1F1A17] transition-colors"><Download className="h-4 w-4" /> Export CSV</a>
          <button onClick={() => setShowHook((v) => !v)} data-testid="partners-hook-toggle" className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] h-10 px-4 text-sm text-[#1F1A17] hover:border-[#1F1A17] transition-colors">Developer hook for hearthvault.family</button>
        </div>
      </div>

      <div className={`mt-4 rounded-2xl border p-4 text-xs leading-relaxed ${data.conversionsReceived ? "border-[#E8DDD3] bg-[#FBF8F4] text-[#685F58]" : "border-dashed border-[#B4532A]/40 bg-[#FDEEE8] text-[#9C421E]"}`} data-testid="partners-hook-status">
        {data.conversionsReceived
          ? `${data.conversionsReceived} conversion events received from hearthvault.family — signups, activations and revenue below are live.`
          : "Clicks and signup clicks are tracked here already. Signups, activations and revenue appear once hearthvault.family sends conversion events — open the developer hook for the 15-line integration."}
      </div>

      {showHook && (
        <div className="mt-4 relative" data-testid="partners-hook-note">
          <button onClick={async () => { await navigator.clipboard.writeText(note); toast.success("Copied developer note"); }} data-testid="partners-hook-copy" className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[#1F1A17] h-8 px-3 text-xs text-[#FBF8F4]"><Copy className="h-3 w-3" /> Copy</button>
          <pre className="rounded-2xl bg-[#181412] text-[#C8BFB8] p-5 text-[11px] leading-relaxed overflow-x-auto whitespace-pre-wrap">{note}</pre>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#B4532A]">Awaiting review ({pending.length})</div>
          <div className="mt-3 space-y-3">{pending.map((p) => <Row key={p.id} p={p} onChange={change} onResendWelcome={resendWelcome} onAddPayout={addPayout} onDeletePayout={deletePayout} onCopyPortal={copyPortal} />)}</div>
        </div>
      )}
      <div className="mt-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Partners ({others.length})</div>
        {others.length === 0 ? <p className="mt-3 text-sm text-[#685F58]">No partners yet. Share <a className="underline" href="/partners">/partners</a> with the creators you'd like to work with.</p>
          : <div className="mt-3 space-y-3">{others.map((p) => <Row key={p.id} p={p} onChange={change} onResendWelcome={resendWelcome} onAddPayout={addPayout} onDeletePayout={deletePayout} onCopyPortal={copyPortal} />)}</div>}
      </div>
    </section>
  );
};
